/*
  __________                   _____            _________   _________                   _____              _____
  ___  ____/_____________________  /__________________  /   __  ____/_____________________  /______ _________  /________
  __  /_   __  ___/  __ \_  __ \  __/  _ \_  __ \  __  /    _  /    _  __ \_  __ \_  ___/  __/  __ `/_  __ \  __/_  ___/
  _  __/   _  /   / /_/ /  / / / /_ /  __/  / / / /_/ /     / /___  / /_/ /  / / /(__  )/ /_ / /_/ /_  / / / /_ _(__  )
  /_/      /_/    \____//_/ /_/\__/ \___//_/ /_/\__,_/      \____/  \____//_/ /_//____/ \__/ \__,_/ /_/ /_/\__/ /____/
  
  This file is designed to serve as the single source of constant variables that are used elsewhere within our app.
  Think of this file as the non-function equivalent of `helpers/utils.js`. Everything in this file should be exported
  so that it can be used in other files. Since we need Git to ignore `config/json` for the most part because it
  contains customized database credentials that vary based on the specific developer, I figured a file like this was
  the best compromise for this sort of functionality.

  To differentiate these exported variables from locally-defined variables in other files, variables should be named
  according to UPPERCASE_SNAKE syntax. Wherever possible, this file should remain organized/alphabetized for parsing
  clarity and to ensure that none of this data is unnecessarily duplicated. To maintain that the data actually remains
  constant, `Object.freeze` should be used when dealing with arrays/objects to ensure their immutability. See the
  following URL for more information*: https://www.freecodecamp.org/news/when-to-capitalize-your-javascript-constants-4fabc0a4a4c4/

  When determining whether data belongs in this file or another one, consider a combination of these criteria:

  1. Is the considered data commonly used across our app? If not, could you at least see it being commonly used in the
  near future?
  2. Would defining the considered data in a single location like this file make development easier down the line?
  3. Can the considered data stand on its own? Meaning that it will not require manipulation (excluding external
  sorting/filtering) after it's been initially defined?

  *I know that this article suggests that UPPERCASE_SNAKE syntax should only be used for primitive values, but since
  (A) `Object.freeze()` is being used to ensure immutability, (B) using one syntax keeps this file more consistent, and
  (C) using UPPERCASE_SNAKE for everything keeps these variables distinct from ones used in other files, I think the
  benefits are enough to ignore that suggestion.


  TODO TAM FUTURE: Add all the Player EventBus event types here.
*/

module.exports.INTERACTION_TYPES = Object.freeze({
  CHECK_ALL_APPLY: 'check_all_apply',
  DEFAULT: 'default',
  DISCUSSION: 'discussion_q',
  FREE_RESPONSE: 'free_response',
  IFRAME: 'iframe_q',
  FILE_VIEWER: 'file_viewer_q',
  MULTIPLE_CHOICE: 'multiple_choice',
  PAUSE: 'pause_q',
  POLL: 'poll_q',
  SECONDARY_STREAM: 'secondary_stream',
  SHORT_ANSWER: 'short_answer',
});

module.exports.CONSOLE_LOG_STYLE_TYPES = Object.freeze({
  Error: Object.freeze([
    'color: #fff',
    'padding: 5px',
    'line-height: 35px',
    'width : 70px',
    'height : 70px',
    'border : 3px dotted red'
  ]),
  Success: Object.freeze([
    'color: #fff',
    'padding: 5px',
    'line-height: 35px',
    'width : 70px',
    'height : 70px',
    'border : 3px solid green'
  ]),
  Warning: Object.freeze([
    'color: #FFF',
    'padding: 5px',
    'line-height: 35px',
    'width : 70px',
    'height : 70px',
    'border : 3px dotted #FFC000'
  ]),
});

module.exports.VIMEO_URLS = Object.freeze({
  VimeoProviderUrl: 'https://player.vimeo.com',
});

module.exports.VIMEO_POST_MESSAGE_EVENTS = Object.freeze({
  Loaded: 'loaded',
  PasswordError: 'PasswordError',
  PrivacyError: 'PrivacyError',
});

module.exports.PLAYER_FADE_IN_TRANSITION_TIMES = Object.freeze({
  Vimeo: Object.freeze({
    zerothInteraction: 1500,
  }),
});

module.exports.MINIMUM_LOOKAROUND_SCHOOLS = Object.freeze([
  512500,517134,517136,
  521626,522016,522190,
  522244,527519,532009,
  534731,534732,534733,
  534734,534735,534736,
  534737,534738,534739,
  534740,536303,536313,
  536316,536320,536321,
  536323,536324,536325,
  536327,536328,536329,
  536330,536331,536334,
  536335,536336,536337,
  536339,536340,536341,
  536342,536349,536350,
  536352,536353,536355,
  536356,536357,536358,
  536359,
]);

module.exports.UPDATE_FAV_ICON_SCHOOLS = Object.freeze([
  2332,
  2336,
  2421
]);


module.exports.PLAYPOSIT_API_ENDPOINTS = Object.freeze({
  // ##################################################
  // ### Legacy-Dash endpoints ########################
  // ##################################################
  instructorDash: ({
    rootState,
    state,
    endpoint,
    route,
    bulb_id: bulbId,
    section_id: sectionId,
    selectedStatus,
    archived,
    timestamp,
  } = {}) => {

    const {
      nodeApiPrefix,
      instructorData: { timezone, },
      school: { id: schoolId, },
      lti13Launch,
    } = rootState;

    const {
      embedded,
      filterString,
    } =  state;

    const {
      page,
      per_page: perPage,
      search,
      sort,
      interaction_id: interactionId,
      interaction_type: interactionType,
      student_id: studentId,
      folder_id: folderId,
      subfolder_id: subfolderId,
    } = state.queryParamsInstructors;

    return `${nodeApiPrefix}/instructor/${endpoint}?page=${page}&per_page=${perPage}&search=${search}&sort=${sort}&bulb_id=${bulbId}&section_id=${sectionId}&interaction_id=${interactionId}&interaction_type=${interactionType}&student_id=${studentId}&folder_id=${folderId}&subfolder_id=${subfolderId}&school_id=${schoolId}&filters=${filterString}&filter_status=${selectedStatus}&archived=${archived}&timestamp=${timestamp}&timezone=${timezone}${route === 'interactions' && embedded ? '&embedded=true' : ''}${lti13Launch ? `&lti13Launch=true` : ''}`
  },
  instructorPlaylistDashData: ({
    nodeApiPrefix,
    sectionId,
    lti13Launch = false,
  } = {}) => {
    return `${nodeApiPrefix}/instructor/section/${sectionId}/playlists${lti13Launch ? '?lti13Launch=true' : ''}`;
  },
  // ##################################################
  // ### LMB endpoints ################################
  // ##################################################
  createLMB: ({
    nodeApiPrefix,
  } = {}) => {
    return `${nodeApiPrefix}/instructor/learnermadebulbs`;
  },
  loadBulbAttemptLMB: ({
    nodeApiPrefix,
    bulbId,
    sectionId,
    resourceLinkId = null,
    lti13Launch = false,
  } = {}) => {
    return `${nodeApiPrefix}/learner/bulbattempt/lmb?bulb_id=${bulbId}&section_id=${sectionId}${resourceLinkId ? `&resourceLinkId=${resourceLinkId}` : ''}${lti13Launch ? `&lti13Launch=true` : ''}`;
  },
  // ##################################################
  // ### LTI endpoints ################################
  // ##################################################
  getLmsData: ({
    nodeApiPrefix,
    ltiLaunchPath,
    consumerPk,
    sectionId,
    lodashInstance,
  } = {}) => {
    const [
      consumerPkQueryVariable,
      sectionIdQueryVariable,
    ] = [
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'consumer_pk'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'section_id')
    ];
    return `${nodeApiPrefix}/${ltiLaunchPath}/lms?${consumerPkQueryVariable}=${consumerPk}&${sectionIdQueryVariable}=${sectionId}`;
  },
  getContent: ({
    nodeApiPrefix,
    ltiLaunchPath,
    resourceLinkId,
    consumerPk: clientId,
    deploymentId = null,
    lodashInstance,
  } = {}) => {
    const [
      resourceLinkIdQueryVariable,
      clientIdQueryVariable, // also known as `consumer_pk` for LTI 1.1
    ] = [
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'resource_link_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'consumer_pk'),
    ];
    return `${nodeApiPrefix}/${ltiLaunchPath}/getcontent?${resourceLinkIdQueryVariable}=${encodeURIComponent(resourceLinkId)}&${clientIdQueryVariable}=${clientId}${deploymentId ? `&deploymentId=${deploymentId}` : ''}`;
  },
  checkLink: ({
    nodeApiPrefix,
    ltiLaunchPath,
    contentId,
    sectionId,
    renderPath, // isn't actually being used in the express logic... will probably remove
    timestamp,
    lodashInstance,
  } = {}) => {
    const [
      contentIdQueryVariable,
      sectionIdQueryVariable
    ] = [
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'content_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'section_id')
    ];
    return `${nodeApiPrefix}/${ltiLaunchPath}/checklink/?${contentIdQueryVariable}=${contentId}&${sectionIdQueryVariable}=${sectionId}&renderPath=${(renderPath)}&timestamp=${timestamp}`;
  },
  setContentLink: ({
    nodeApiPrefix,
    ltiLaunchPath,
    resourceLinkId,
    contentId,
    sectionId,
    consumerPk,
    enableGrading = 0,
    renderPath,
    groupSetId = null,
    lodashInstance,
  } = {}) => {
    const [
      resourceLinkIdQueryVariable,
      consumerPkQueryVariable,
      sectionIdQueryVariable,
      contentIdQueryVariable,
      renderPathQueryVariable,
      enableGradingQueryVariable,
      groupSetIdQueryVariable
    ] = [
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'resource_link_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'consumer_pk'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'section_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'content_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'render_path'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'enable_grading'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'group_set_id')
    ];
    return `${nodeApiPrefix}/${ltiLaunchPath}/setcontentlink?${resourceLinkIdQueryVariable}=${encodeURIComponent(resourceLinkId)}&${consumerPkQueryVariable}=${consumerPk}&${sectionIdQueryVariable}=${sectionId}&${contentIdQueryVariable}=${contentId}&${renderPathQueryVariable}=${renderPath}&${enableGradingQueryVariable}=${enableGrading}${(groupSetId) ? `&${groupSetIdQueryVariable}=${groupSetId}` : ''}`;
  },
  unlinkBulb: ({
    nodeApiPrefix,
    ltiLaunchPath,
    bulbId,
    sectionId,
    contentItemLaunch,
    renderPath,
    timestamp,
    lodashInstance,
  } = {}) => {
    const [
      bulbIdQueryVariable,
      sectionIdQueryVariable,
      renderPathQueryVariable,
    ] = [
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'bulb_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'section_id'),
      generatePathOrQueryVariable(lodashInstance.camelCase, ltiLaunchPath, 'render_path')
    ];
    return `${nodeApiPrefix}/${ltiLaunchPath}/unlinkbulb/?${bulbIdQueryVariable}=${bulbId}&${sectionIdQueryVariable}=${sectionId}&${renderPathQueryVariable}=${renderPath}&isContentItemLaunch=${contentItemLaunch}&timestamp=${timestamp}`;
  },
  unlinkPlaylist: ({
    nodeApiPrefix,
    ltiLaunchPath,
    playlistId,
    sectionId,
  } = {}) => {
    return `${nodeApiPrefix}/${ltiLaunchPath}/unlink/playlist/${playlistId}/section/${sectionId}`;
  },
  checkLti1p1ContentAlreadyLinked: ({
    nodeApiPrefix,
    contextId,
    resourceLinkId,
    consumerVersion,
  } = {}) => {
    return `${nodeApiPrefix}/lti13dash/${consumerVersion}/context/${contextId}/content/${resourceLinkId}/linked`;
  },
  checkLtiContentMigration: ({
    nodeApiPrefix
  } = {}) => {
    return `${nodeApiPrefix}/lti13dash/contentmigration`;
  },
  // ##################################################
  // ### Player endpoints #############################
  // ##################################################
  learnerPreview: ({
    url,
    bulbId,
    sectionId,
    resourceLinkId,
    renderPath,
    playlistId,
    moduleId,
    lti13Launch = false,
  } = {}) => {
    return `${url}neoplayer/bulb?bulb_id=${bulbId}&section_id=${sectionId}&resource_link_id=${resourceLinkId}&render_path=${renderPath}&playlist_id=${playlistId}&module_id=${moduleId}&${(lti13Launch) ? `lti13Launch=true` : ''}`
  },
  teacherPreview: ({
    url,
    bulbId,
    sectionId,
  } = {}) => {
    return `${url}neoplayer/bulb/preview/?bulb_id=${bulbId}&section_id=${sectionId}`;
  },
  anonymousPreview: ({
    url,
    bulbId,
  } ={}) => {
    return `${url}neoplayer/preview?bulb_id=${bulbId}`;
  },
  parentPreview: ({
    url,
    bulbId,
    sectionId,
    studentId,
  } = {}) => {
    return `${url}neoplayer/bulb/parent?bulb_id=${bulbId}&section_id=${sectionId}&observer=${studentId}`;
  },
  broadcastPreview: ({
    url,
    bulbId,
    sectionId,
    resourceLinkId,
    renderPath,
    lti13Launch = false,
  } = {}) => {
    return `${url}neoplayer/bulb/broadcast/?bulb_id=${bulbId}&section_id=${sectionId}&resource_link_id=${resourceLinkId}&render_path=${renderPath}${lti13Launch ? `&lti13Launch=true` : ''}`;
  },
  // ##################################################
  // ### Playlist endpoints ###########################
  // ##################################################
  playerStudentRedirect: ({
    baseURL,
    sectionId,
    bulbId,
    userToken,
    nsf,
    playlistId,
    moduleId,
    scoreThreshold,
    resourceLinkId,
    lmsConsumerVersion,
    lti13Launch = false,
    lmsIssuer,
  } = {}) => {
    return `${baseURL}player_v2/?section_id=${sectionId}&type=bulb&bulb_id=${bulbId}&token=${userToken}&playlist=true${nsf ? `&playlist_id=${playlistId}&module_id=${moduleId}` : ''}${(scoreThreshold) ? `&scoreThreshold=${scoreThreshold}` : ''}${(resourceLinkId) ? `&playlistResourceLinkId=${resourceLinkId}` : ''}${(lmsConsumerVersion) ? `&playlistLmsConsumerVersion=${lmsConsumerVersion}` : ''}${lti13Launch ? `&lti13Launch=true` : ''}${lmsIssuer ? `&issuer=${lmsIssuer}` : ''}`;
  },
  playerInstructorRedirect: ({
    baseURL,
    sectionId,
    bulbId,
    userToken,
    scoreThreshold,
    resourceLinkId,
    lmsConsumerVersion,
    lti13Launch = false,
  } = {}) => {
    return `${baseURL}player_v2/?type=share${(sectionId) ? `&section_id=${sectionId}` : ''}&bulb_id=${bulbId}&token=${userToken}&playlist=true${(scoreThreshold) ? `&scoreThreshold=${scoreThreshold}` : ''}${(resourceLinkId) ? `&playlistResourceLinkId=${resourceLinkId}` : ''}${(lmsConsumerVersion) ? `&playlistLmsConsumerVersion=${lmsConsumerVersion}` : ''}${lti13Launch ? `&lti13Launch=true` : ''}`;
  },
});

module.exports.LTI_CONTENT_LINKED_STATUSES = Object.freeze({
  Linked: 'linked',
});

module.exports.PLAYPOSIT_PHP_ENDPOINTS = Object.freeze({
  LTI_11: {
    contentItemSelectionRequestDisablePlacement: ({
      phpApiPrefix,
      teacherId,
      lessonId,
      consumerPk,
      lessonTitle,
      renderPath,
      enableGrading,
    } = {}) => {
      return `${phpApiPrefix}/LTI/contentItemLaunch/?teacher_id=${teacherId}&lesson_id=${lessonId}&context_pk=${consumerPk}&placement_type=frame&placement_height=600&placement_width=800&render_path=${renderPath}&lesson_title=${encodeURIComponent(lessonTitle)}&lti_enable_grading=${enableGrading}`;
    },
    contentItemSelectionRequestPlacement: ({
      phpApiPrefix,
      teacherId,
      lessonId,
      consumerPk,
      placement,
      placementWidth = '800',
      placementHeight = '600',
      lessonTitle,
      renderPath,
      enableGrading,
    } = {}) => {
      return `${phpApiPrefix}/LTI/contentItemLaunch/?teacher_id=${teacherId}&lesson_id=${lessonId}&context_pk=${consumerPk}&placement_type=${placement}&placement_height=${placementHeight}&placement_width=${placementWidth}&render_path=${renderPath}&lesson_title=${encodeURIComponent(lessonTitle)}&disable_placement=0&lti_enable_grading=${enableGrading}`;
    },
  },
  LTI_13: {
    contentItemSelectionRequestDisablePlacement: ({
      phpApiPrefix,
      teacherId,
      contentId,
      contentTitle,
      renderPath,
      schoolId = null,
    } = {}) => {
      return `${phpApiPrefix}/LTI13/contentItemLaunch/?teacherId=${teacherId}&contentId=${contentId}&placementType=iframe&placementHeight=600&placementWidth=800&renderPath=${renderPath}&contentTitle=${encodeURIComponent(contentTitle)}${(schoolId) ? `&schoolId=${schoolId}`: ''}`;
    },
    contentItemSelectionRequestPlacement: ({
      phpApiPrefix,
      teacherId,
      contentId,
      contentTitle,
      placement = 'iframe',
      placementWidth = '800',
      placementHeight = '600',
      renderPath,
      schoolId = null,
    } = {}) => {
      if (placement === 'iframe') {
        return `${phpApiPrefix}/LTI13/contentItemLaunch/?teacherId=${teacherId}&contentId=${contentId}&placementType=${placement}&placementHeight=${placementHeight}&placementWidth=${placementWidth}&renderPath=${renderPath}&contentTitle=${encodeURIComponent(contentTitle)}${(schoolId) ? `&schoolId=${schoolId}`: ''}`;
      }
      return `${phpApiPrefix}/LTI13/contentItemLaunch/?teacherId=${teacherId}&contentId=${contentId}&placementType=${placement}&renderPath=${renderPath}&contentTitle=${encodeURIComponent(contentTitle)}${(schoolId) ? `&schoolId=${schoolId}`: ''}`;
    },
    ltiAccountChange: (phpApiPrefix) => {
      return `${phpApiPrefix}/account-change`;
    },
  }
});


module.exports.LTI_RENDER_PATHS = Object.freeze({
  Play2: 'play2',
  Studentmade: 'studentmade',
  Studentmade2: 'studentmade2',
  Playlist: 'playlist',
  Broadcast: 'broadcast',
  Broadcast2: 'broadcast2',
});

module.exports.LMS_TYPES = Object.freeze({
  Canvas: 'Canvas',
});

module.exports.LTI_LAUNCH_ENDPOINTS = Object.freeze({
  LTI_11: 'LTI/launch',
  LTI_13: 'LTI_13/launch',
});

module.exports.LTI11_LAUNCH_MESSAGE_TYPES = Object.freeze({
  BasicLtiLaunchRequest: 'basic-lti-launch-request',
  BasicLaunch: 'Basic_Launch',
});

module.exports.LTI13_LAUNCH_MESSAGE_TYPES = Object.freeze({
  LtiResourceLinkRequest: 'LtiResourceLinkRequest',
  LtiDeepLinkingRequest: 'LtiDeepLinkingRequest',
  LtiSubmissionReviewRequest: 'LtiSubmissionReviewRequest',
});

module.exports.LTI13_MIGRATION_STATUSES = Object.freeze({
  Success: 'success',
  Error: 'error',
  Idle: 'idle',
});

module.exports.LTI_GRADE_SYNC_STATUS_MESSAGE = Object.freeze({
  LTI_11: {
    Success: 'Successfully Exported Grades.',
  },
  LTI_13: {
    Success: 'Successfully Exported LTI 1.3 Grades!'
  }
});

module.exports.PLAYPOSIT_LMB_LAUNCH_ENDPOINTS = Object.freeze({
  LMB_LAUNCH: 'launch/lmb',
  LTI_11_LMB_LAUNCH: 'LTI/launch/lmb',
  LTI_13_LMB_LAUNCH: 'LTI13/launch/lmb',
});

module.exports.DESIGNER_PLAYER_VIDEO_INTEGRATION_TYPES = Object.freeze({
  Vimeo: 'video/vimeo',
  YouTube: 'video/youtube',
});

module.exports.PLAYER_VIDEO_INTEGRATION_TYPES = Object.freeze({
  Vimeo: 'video/vimeo',
  YouTube: 'video/youtube',
});


/**
 * Generate the correct path variable, depending on what LTI launch is being hit. -.-
 * 
 * @param {Function} camelCaseFn 
 * @param {String} ltiLaunch 
 * @param {String} pathVariable 
 * 
 * @returns {String} pathVariable
 */
function generatePathOrQueryVariable(camelCaseFn, ltiLaunch, pathVariable) {
  return (ltiLaunch === 'ltidash') ? pathVariable : camelCaseFn(pathVariable);
}
